import React, { useContext, useEffect, useState } from 'react'

import { Add } from '@mui/icons-material'
import clsx from 'clsx'
import { isEqual, sumBy, uniq } from 'lodash'

import MainModal from 'web/src/components/modals/common/MainModal'
import { IconEmpty } from '../icons/IconEmpty'
import { IconDownload } from '../icons/IconDownload'
import { JudgeItem } from './components'
import { ScoringModalTotalPoints } from './components/Operations/ScoringModalTotalPoints/ScoringModalTotalPoints'
import { ScoringRiderInfo } from './components/Operations/ScoringRiderInfo/ScoringRiderInfo'
import { JudgesSignature } from './components/Operations/JudgesSignature/JudgesSignature'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { ScoreStatusSelect } from './components/ScoreStatusSelect'

// Redux
import { useAppSelector } from '../../store/hooks'
import { selectProfileData } from '../../store/user/userSlice'

// Helpers
import useToasterHelper from '../../helpers/ToasterHelper'
import { getUserFullName } from '../../helpers/helpers'
import { getConvertedData } from '../../models/interface.helper'
import {
  getJudgesAndSheet,
  sortByTotalPoints,
  sortResultsByTime,
} from '../../pages/organizer/manage/ManageClinicNOtherOperationsTab/data/operations-tab-utils'
import { httpService } from '../../services/httpService'
import { getCurrency } from '../../helpers/payment'
import { cn } from '../../lib/utils'

// PDF Templates
import { createScoringResultsPdf } from '../../templates/pdf/reports/scoring-results/scoring-results'

// Services
import FirestoreService from '../../services/firestoreService'
import UserService from '../../services/firebase-service/users/userService'
import RegistrationDyDayService from '../../services/firebase-service/registration-by-day/RegistrationDyDayService'
import { HandleModalContext } from '../../layout/mainlayout/MainLayout'

// Models
import {
  IScoreCardDiscipline,
  RegistrationFeesType,
} from '../../models/event-fees/event-fees.interface'
import { EventFeesModel } from '../../models/event-fees/event-fees.model'
import { IEventReviewPublish } from '../../models/event-review-publish/event-review-publish.interface'
import { EventReviewPublishModel } from '../../models/event-review-publish/event-review-publish.model'
import { IEventStaffInterface } from '../../models/event-staff/event-staff.interface'
import { EventStaffModel } from '../../models/event-staff/event-staff.model'
import { IRegistrationByDayInterface } from '../../models/registrations-by-day/registrationByDay.interface'
import { IUserInterface } from '../../models/users/user.interface'
import { UserModel } from '../../models/users/user.model'

//Constants
import { CONST } from '../../const/const'
import { MESSAGES_CONST } from '../../const/messages-const'
import { MODAL_CONSTS } from '../../const/modal-const'

import { dummySheets } from '../../pages/organizer/manage/data/sheets'

type ScoringModalProps = {
  dataToPassOn: {
    editable?: boolean
    onSaveScoring?: () => Promise<void>
    onChangeJudge?: (
      row: RegistrationFeesType,
      values: { label: string; value: string }[]
    ) => Promise<void>
    rider: IRegistrationByDayInterface
    prizeMoney?: string
    publishPrize?: boolean
  }
}

interface IUserInterfaceNew extends IUserInterface {
  selected?: boolean
}

type RegistrationFeesEx = RegistrationFeesType & { parentId: string | null }

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

const ScoringModal = ({ dataToPassOn }: ScoringModalProps) => {
  const { rider, onSaveScoring, onChangeJudge, editable, publishPrize, prizeMoney } = dataToPassOn

  const handleModalContext = useContext(HandleModalContext)

  const [row, setRow] = useState<IRegistrationByDayInterface>(rider)
  const [file, setFile] = useState<string | null>(rider?.score?.file ?? null)

  const [maxPoints, setMaxPoints] = useState(0)
  const [totalPoints, setTotalPoints] = useState(0)
  const [feedback, setFeedback] = useState<string>(rider?.score?.details ?? '')
  const [loading, setLoading] = useState(false)
  const [users, setUsers] = useState<IUserInterface[] | null>(null)
  const [publishedEvent, setPublishedEvent] = useState<IEventReviewPublish | null>(null)
  const [fee, setFee] = useState<RegistrationFeesEx | null>(null)
  const [isLocked, setIsLocked] = useState(false)

  const toastFunctions = useToasterHelper()
  const profileData = useAppSelector(selectProfileData)

  const currency = getCurrency(publishedEvent?.EventPaymentSettings?.currency)

  const handleSave = async () => {
    setLoading(true)
    const registrationsByDay = await RegistrationDyDayService.getByEventIds([rider.eventId ?? ''])

    let registrationsByDayUpdated: IRegistrationByDayInterface[] =
      registrationsByDay?.map((registrationByDay) => {
        if (registrationByDay.id === row.id) {
          return {
            ...registrationByDay,
            score: {
              ...row.score,
              totalPoints: row.score?.sheet?.name ? totalPoints : (row.score?.totalPoints ?? null),
              details: feedback ?? '',
              file: file ?? null,
            },
          }
        } else {
          return registrationByDay
        }
      }) ?? []

    const uniqueRegistrationsByDay = registrationsByDayUpdated.reduce(
      (acc: IRegistrationByDayInterface[], currentItem) => {
        // Check if the riderId of the current item is already in the accumulator
        const isRiderIdExist = acc.some(
          (item) =>
            item.uuid === currentItem.uuid &&
            item.riderId === currentItem.riderId &&
            isEqual(item.horsesSelected, currentItem.horsesSelected)
        )

        // If riderId doesn't exist, add the current item to the accumulator
        if (!isRiderIdExist) {
          acc.push(currentItem)
        }
        return acc
      },
      []
    )

    let sortedRegistrations: IRegistrationByDayInterface[]
    const scoreCardType = uniqueRegistrationsByDay?.[0]?.score?.judges?.[0]?.scoreCardType
    if (scoreCardType === 'fastest') {
      sortedRegistrations = sortResultsByTime(
        uniqueRegistrationsByDay,
        fee?.isAsc ? 'asc' : 'desc',
        'fastest'
      )
    } else if (scoreCardType === 'optimum') {
      sortedRegistrations = sortResultsByTime(
        uniqueRegistrationsByDay,
        fee?.isAsc ? 'asc' : 'desc',
        'optimal'
      )
    } else {
      sortedRegistrations = sortByTotalPoints(uniqueRegistrationsByDay, fee?.isAsc ? 'asc' : 'desc')
    }

    let count = 0

    const registrationsByDayToUpdate: IRegistrationByDayInterface[] = []

    // update rank for all registrations that have the same class
    sortedRegistrations?.forEach((registrationByDay) => {
      if (rider.uuid === registrationByDay.uuid) {
        count++
        const current = {
          ...registrationByDay,
          ...(registrationByDay.score && {
            score: { ...registrationByDay.score, rank: registrationByDay.isScratched ? 0 : count },
          }),
        }
        registrationsByDayToUpdate.push(current)
        return current
      } else {
        return registrationByDay
      }
    })

    // Update firestore collection on BE by batch
    if (registrationsByDayToUpdate?.[0]) {
      await httpService({
        url: 'update_registrations_by_day',
        method: 'POST',
        data: {
          registrationsByDay: registrationsByDayToUpdate,
        },
      })
    }

    if (onSaveScoring) await onSaveScoring()

    toastFunctions.success({ message: MESSAGES_CONST.EVENT_SCORE_UPDATED })
    setLoading(false)
    handleModalContext?.handleModal?.(false, MODAL_CONSTS.SCORING)
  }

  useEffect(() => {
    if (row.score?.sheet?.name && row.score?.judges && row.score.judges.length > 0) {
      setMaxPoints(sumBy(row.score.judges[0].sections, (section) => section.max))

      let totalPoints_ = 0
      row.score.judges.forEach((judge) => {
        judge.sections.forEach((section) =>
          section.rows.forEach((row) => {
            if (row.coefficient) {
              totalPoints_ += Number(row.coefficient) * Number(row.score)
            } else if (row.fraction === 'minus') {
              totalPoints_ -= Number(row.score)
            } else {
              totalPoints_ += Number(row.score)
            }
          })
        )

        totalPoints_ -= judge.errors ?? 0
      })

      setTotalPoints(
        Math.round((totalPoints_ / row.score.judges.filter((judge) => judge.id).length) * 1000) /
          1000
      )
    } else {
      setMaxPoints(0)
    }
  }, [row])

  useEffect(() => {
    if (Number(row.score?.totalPoints) > 0) {
      setIsLocked(true)
    }
  }, [row.score?.totalPoints])

  const getEventAndUsers = async () => {
    const users_: IUserInterface[] = []

    if (row.eventId) {
      const publishedEventSnapShot = await FirestoreService.getItem(
        CONST.DATA.FIRESTORE.V01.COLLECTIONS.EVENT_REVIEW_PUBLISH.NAME,
        row.eventId
      )

      const publishedEvent_ =
        EventReviewPublishModel.fromFirestoreDoc(publishedEventSnapShot).toObject()
      setPublishedEvent(publishedEvent_)

      const eventFees = new EventFeesModel(publishedEvent_?.EventFees)

      let currentFee: RegistrationFeesEx | null = null

      eventFees?.registrationFees?.forEach((registrationFee) => {
        if (registrationFee?.uuid === row.uuid) {
          currentFee = { ...registrationFee, parentId: null }
        } else {
          registrationFee.children?.forEach((child) => {
            if (child?.uuid === row.uuid) {
              currentFee = { ...child, parentId: registrationFee.uuid }
            }
          })
        }
      })
      setFee(currentFee || null)

      const staffs: IEventStaffInterface = new EventStaffModel(
        publishedEvent_?.EventStaff
      ).toFirestore()

      const judgesStaff_ = staffs.eventStaff?.find((staff) => staff.title === 'Judges')

      if (judgesStaff_?.value && judgesStaff_.value.length > 0) {
        const usersSnap = await FirestoreService.getItemsUsingIds(
          COLLECTIONS.USERS.NAME,
          judgesStaff_.value
        )

        usersSnap.forEach((curr) => {
          users_.push(getConvertedData(UserModel.fromFirestoreDoc(curr).toObject()))
        })

        const usersWithSelection = users_.map((user) => {
          const current = row.score?.judges?.find((judge) => judge.id === user.id)
          if (current?.id) {
            return { ...user, selected: true }
          } else {
            return { ...user, selected: false }
          }
        })

        setUsers(usersWithSelection)
      }
    }
  }

  useEffect(() => {
    getEventAndUsers().then()
  }, [row.eventId])

  const onSaveJudge = async (current: IUserInterface[]) => {
    try {
      const usersIds = current.map((user) => user.id)
      const newJudges: string[] = []
      const newEventStaff = publishedEvent?.EventStaff?.eventStaff?.map((eventStaff) => {
        if (eventStaff.id === 5 && eventStaff.title === 'Judges') {
          const uniqValues = uniq([...eventStaff.value, ...usersIds])
          newJudges.push(...uniqValues)
          return {
            ...eventStaff,
            value: newJudges,
          }
        } else {
          return eventStaff
        }
      })

      await FirestoreService.updateItem(COLLECTIONS.EVENT_STAFF.NAME, row.eventId, {
        eventStaff: newEventStaff,
      })

      await FirestoreService.updateItem(COLLECTIONS.EVENT_REVIEW_PUBLISH.NAME, row.eventId, {
        ...publishedEvent,
        EventStaff: { ...publishedEvent?.EventStaff, eventStaff: newEventStaff },
      })

      const values: { label: string; value: string }[] = current.map((user) => ({
        label: getUserFullName(user),
        value: user.id,
      }))

      if (onChangeJudge && fee) await onChangeJudge(fee, values)
    } catch (error: any) {
      console.error(error)
      toastFunctions.error({
        message: error?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onAddJudge = async () => {
    handleModalContext?.handleModal?.(false, MODAL_CONSTS.SCORING)
    handleModalContext?.handleModal?.(true, MODAL_CONSTS.ORGNAIZER_EVENT_STAFF_ADD, {
      ...handleModalContext?.dataToPassOn,
      id: 5,
      row: users?.filter((user: IUserInterfaceNew) => user.selected),
      showAddNewPersonFeature: true,
      onSaveStaff: async (current: { selected: IUserInterface[] }) => {
        await onSaveJudge(current.selected)
        handleModalContext?.handleModal?.(false, MODAL_CONSTS.ORGNAIZER_EVENT_STAFF_ADD)
      },
    })
  }

  const showLockedModal = () => {
    handleModalContext?.handleModal?.(true, MODAL_CONSTS.RESULTS_LOCKED, {
      ...dataToPassOn,
      setIsLocked,
    })
  }

  const generateResultsPDF = async () => {
    try {
      if (!publishedEvent || !row || !users) {
        toastFunctions.error({ message: 'Missing required data for PDF generation' })
        return
      }

      // Generate PDF using the publishedEvent directly
      await createScoringResultsPdf(publishedEvent, row, users)
    } catch (error) {
      console.error('Error generating PDF:', error)
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    }
  }

  let max = 0
  if (row.score?.judges) {
    max = sumBy(row.score.judges[0]?.sections, (section) => section.max)
  }

  const showResults = profileData?.id && fee?.discipline === 'Dressage'

  return (
    <MainModal
      customTitle={
        <div className={cn('flex-1 flex items-center gap-3 mb-2', showResults && 'my-4')}>
          <div>
            <div className="text-xl xl:text-2xl text-SeabiscuitDark200ThemeColor">Results</div>
          </div>
          {showResults && (
            <div className="ml-auto">
              <button
                className="group flex items-center gap-2 px-4 py-2 text-sm text-black bg-gray-200 hover:bg-SeabiscuitMainThemeColor/10 hover:text-SeabiscuitMainThemeColor rounded-full transition-colors"
                onClick={generateResultsPDF}
              >
                <IconDownload className="w-4 h-4 text-black group-hover:text-SeabiscuitMainThemeColor transition-colors" />
                Download Results
              </button>
            </div>
          )}
        </div>
      }
      size="2xl"
      modalClassName={'!z-[30]'}
      overlayClassName={'!z-[20]'}
      type="SCORING"
      show={true}
      buttons={[
        {
          hidden: !editable || !!(!row.score?.judges && row.score?.sheet?.name),
          loading,
          label: 'Save & Lock',
          bgClass: 'bg-SeabiscuitMainThemeColor',
          textClass: 'text-white',
          onClick: handleSave,
          borderClass: 'border !border-SeabiscuitMainThemeColor !w-full',
          onHoverClass: 'hover:bg-[#d70443]',
          className: 'text-base',
        },
        {
          label: 'Cancel',
          hidden: !!(!row.score?.judges && row.score?.sheet?.name),
          bgClass: 'bg-transparent',
          textClass: 'text-SeabiscuitMainThemeColor',
          onClick: () => handleModalContext?.handleModal?.(false, MODAL_CONSTS.SCORING),
          borderClass: 'border !border-SeabiscuitMainThemeColor !w-full',
          onHoverClass: 'hover:text-white hover:bg-SeabiscuitMainThemeColor',
          className: 'text-base',
        },
      ]}
    >
      <div className="text-SeabiscuitDark200ThemeColor ">
        {row?.id && (
          <ScoringRiderInfo
            row={row}
            setRow={setRow}
            setFile={setFile}
            file={file}
            publishPrize={publishPrize || false}
            prizeMoney={prizeMoney || 0}
            editable={editable ?? false}
            currency={currency}
          />
        )}
        <hr />
        <div className="flex flex-wrap items-center gap-3 justify-between my-4">
          {(row.score?.sheet?.name || fee?.scoreCard === 'test-of-choice') && (
            <div className="flex flex-1 flex-wrap justify-between items-center gap-2 min-w-[250px]">
              {fee?.scoreCard === 'test-of-choice' && (
                <Select
                  value={row.score?.sheet?.id || undefined}
                  onValueChange={async (value) => {
                    const currentSheet = dummySheets.find((item) => item.id === value)
                    if (currentSheet) {
                      const usersInDb = await UserService.getByIds(fee?.judges || [])

                      const { judges, sheet } = await getJudgesAndSheet({
                        discipline: fee?.discipline as IScoreCardDiscipline,
                        sheetId: currentSheet?.id,
                        judgeIds: fee?.judges || [],
                        users: usersInDb,
                      })

                      const updatedRow = {
                        ...row,
                        score: {
                          ...row.score,
                          totalPoints: null,
                          rank: null,
                          sheet,
                          judges,
                        },
                      }
                      setRow(updatedRow)
                    }
                  }}
                >
                  <SelectTrigger className="p-0 data-[size=default]:h-auto text-left leading-normal whitespace-normal text-[25px] font-bold data-[placeholder]:text-SeabiscuitDark200ThemeColor placeholder:text-SeabiscuitDark200ThemeColor border-none">
                    <SelectValue placeholder="Select test" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[500px]">
                    {dummySheets?.map((day) => (
                      <SelectItem key={day.id} value={day.id}>
                        {day.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {row.score?.sheet?.name && fee?.scoreCard !== 'test-of-choice' && (
                <h3 className="text-[25px] font-bold">{row.score.sheet.name}</h3>
              )}
            </div>
          )}
          <ScoreStatusSelect
            value={row}
            setValue={setRow}
            showLockedModal={showLockedModal}
            isLocked={isLocked}
            editable={editable}
          />
        </div>
        <ScoringModalTotalPoints
          row={row}
          results={row.score}
          totalPoints={totalPoints}
          maxPoints={maxPoints}
          max={max}
          fee={fee}
        />
        <hr />
        {(row.score?.sheet?.name || fee?.scoreCard === 'test-of-choice') &&
          (row.score?.judges && row.score.judges.length > 0 ? (
            <div className="mt-4 flex flex-col gap-2">
              {row.score.judges.map(
                (judge, judgeIndex) =>
                  judge.id && (
                    <JudgeItem
                      key={judgeIndex}
                      judge={judge}
                      judgeIndex={judgeIndex}
                      row={row}
                      setRow={setRow}
                      editable={editable ?? false}
                      isLocked={isLocked}
                      showLockedModal={showLockedModal}
                      maxPoints={maxPoints}
                    />
                  )
              )}
              {editable && (
                <button
                  className="flex gap-2 items-center justify-end mt-2 text-base text-SeabiscuitMainThemeColor hover:opacity-70"
                  onClick={async () => {
                    if (isLocked) showLockedModal()
                    else await onAddJudge()
                  }}
                >
                  <Add
                    fontSize="small"
                    className="border rounded-full border-SeabiscuitMainThemeColor"
                  />
                  Add Judge
                </button>
              )}
            </div>
          ) : editable ? (
            <div className="py-10 px-4 flex items-center justify-center flex-col">
              <p>You must add a judge to load scoring form</p>
              <div className="w-[138px] py-10 mx-auto">
                <IconEmpty className="w-full" />
              </div>
              <button
                className="flex gap-2 items-center justify-center text-base text-SeabiscuitMainThemeColor hover:opacity-70"
                onClick={async () => {
                  if (isLocked) showLockedModal()
                  else await onAddJudge()
                }}
              >
                <Add
                  fontSize="small"
                  className="border rounded-full border-SeabiscuitMainThemeColor"
                />
                Add Judge
              </button>
            </div>
          ) : (
            <div className="py-10 px-4 flex items-center justify-center flex-col">
              <p>No Added Judges</p>
            </div>
          ))}

        {!row.score?.judges ||
          (!row.score?.sheet?.name && fee?.scoreCard !== 'test-of-choice' && (
            <textarea
              disabled={!editable}
              placeholder={editable ? 'Enter notes' : 'No notes'}
              className={clsx(
                'rounded-lg border-[#D3DAEE] w-full h-72 mt-4',
                !editable && 'bg-[#d3daee50]'
              )}
              value={feedback}
              onChange={(e) => {
                if (isLocked) {
                  showLockedModal()
                } else {
                  setFeedback(e.target.value)
                }
              }}
            />
          ))}
        {editable &&
          row.score?.judges?.[0] &&
          !row.score?.sheet?.name &&
          fee?.scoreCard !== 'test-of-choice' && (
            <JudgesSignature
              isLocked={isLocked}
              showLockedModal={showLockedModal}
              row={row}
              setRow={setRow}
              judgeIndex={0}
            />
          )}
      </div>
    </MainModal>
  )
}

export default ScoringModal
